import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type PersonOrientation = 'vertical' | 'horizontal';
export type PersonVariant = 'circular' | 'square';

/**
 * Renders a person's profile card with a photo, name, professional title, description,
 * and social network links. Use on team pages, author bios, speaker directories, and
 * contact profiles on both marketing and application surfaces.
 *
 * Two layout orientations are supported: `horizontal` (image to the left) and `vertical`
 * (image above text). The `variant` property controls both image shape and display mode:
 * `circular` renders a fixed-size 96px/80px avatar clipped to a full circle; `square` renders
 * a full-width edge-to-edge photo with square corners (the "Image top" Figma variant). All
 * content is passed via named slots so it stays in light DOM and is fully SEO-crawlable.
 * Every slot is optional — wrappers hide automatically when their slot is empty.
 *
 * @element asc-person
 *
 * @slot image       - The person's photo. Accepts a single `<img>`. Hidden when empty.
 * @slot name        - Full name. Wrap in an appropriate heading (`<h2>`–`<h4>`).
 * @slot title       - Professional role or position. Use a `<p>` or `<span>`.
 * @slot description - Short bio. Accepts `<p>` elements.
 * @slot socials     - Social network links. Accepts `<a href>` elements (optionally wrapping SVG icons).
 *   Each link should carry an `aria-label` identifying the network (e.g., `aria-label="Jane Doe on LinkedIn"`).
 *
 * @csspart base         - Root `<article>` element; top-level flex layout container.
 * @csspart image-frame  - `<figure>` wrapping the image slot; controls dimensions, shape, and border.
 * @csspart content      - Flex column wrapping the text group and the socials row.
 * @csspart name         - Wrapper `<div>` around the `name` slot.
 * @csspart title        - Wrapper `<div>` around the `title` slot.
 * @csspart description  - Wrapper `<div>` around the `description` slot.
 * @csspart socials      - Flex-row wrapper `<div>` around the `socials` slot.
 *
 * @cssprop --asc-person-image-size            - Width and height of the avatar image frame (`variant="circular"`). Responsive default: `96px` (desktop, container >=384px), `80px` (mobile, container <384px). Override to lock a fixed size.
 * @cssprop --asc-person-image-radius          - Border radius of the image frame when `variant="square"`. Default: `var(--asc-radius-none)`
 * @cssprop --asc-person-image-aspect-ratio    - Aspect ratio of the image frame when `variant="square"`. Default: `1/1` (matches Figma — always square). Override to use a different ratio.
 * @cssprop --asc-person-image-border-color    - Image frame border color (`variant="circular"` only). Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-person-image-border-width    - Image frame border width (`variant="circular"` only). Default: `var(--asc-border-width)`
 * @cssprop --asc-person-gap                   - Gap between image frame and content block. Responsive default: `var(--asc-spacing-2xl)` (desktop, container ≥384px), `var(--asc-spacing-xl)` (mobile, container <384px). Override to lock a fixed gap.
 * @cssprop --asc-person-content-gap           - Gap between the text group and the socials row. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-person-text-group-gap        - Gap between the name+title pair and the description. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-person-name-color            - Name text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-person-name-font-size        - Name font size. Default: `var(--asc-font-size-text-lg)`
 * @cssprop --asc-person-name-font-weight      - Name font weight. Default: `var(--asc-font-weight-bold)`
 * @cssprop --asc-person-title-color           - Role/title text color. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-person-title-font-size       - Role/title font size. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-person-description-color     - Description text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-person-description-font-size - Description font size. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-person-social-color          - Social link default icon color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-person-social-color-hover    - Social link hover/focus-visible icon color. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-person-social-opacity        - Social link default opacity. Default: `0.5`
 * @cssprop --asc-person-social-opacity-hover  - Social link opacity on hover. Default: `1`
 * @cssprop --asc-person-social-icon-size      - Social icon link touch target size. Default: `24px` (meets WCAG 2.5.8 AA 24×24px minimum)
 * @cssprop --asc-person-social-gap            - Gap between individual social link items. Default: `var(--asc-spacing-xl)` (circular/horizontal), `var(--asc-spacing-2xl)` (vertical+square)
 * @cssprop --asc-person-social-transition     - Transition duration/easing for social link hover states. Default: `150ms ease`
 */
@customElement('asc-person')
export class AscPerson extends LitElement {
  static override styles = css`
    :host {
      display: block;
      container-type: inline-size;
      font-family: var(--asc-font-family-body);

      /*
       * Internal responsive defaults — overridden by the container query below.
       * Consumer tokens (--asc-person-image-size, --asc-person-gap) always win
       * as the outer fallback layer.
       */
      --_avatar-size: 96px; /* no-token: 96px — Figma desktop */
      --_outer-gap: var(--asc-spacing-2xl); /* 20px — Figma desktop */
    }

    /*
     * Mobile breakpoint: matches Figma's "Mobile" column (container < 384px).
     * Avatar shrinks to 80px, outer gap reduces to spacing-xl (16px).
     */
    @container (max-width: 383px) {
      :host {
        --_avatar-size: 80px; /* no-token: 80px — Figma mobile */
        --_outer-gap: var(--asc-spacing-xl); /* 16px — Figma mobile */
      }
    }

    /* ── Base layout ──────────────────────────────────────────── */
    [part='base'] {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: var(--asc-person-gap, var(--_outer-gap));
    }

    /* vertical orientation always stacks */
    :host([orientation='vertical']) [part='base'] {
      flex-direction: column;
    }

    /*
     * Max-width constraints — matches Figma canvas widths.
     * Circular (any orientation) + horizontal+square: 384px desktop, 304px mobile.
     * Vertical+square (Image top):                    296px desktop, 343px mobile.
     */
    :host([variant='circular']),
    :host([orientation='horizontal'][variant='square']) {
      max-width: 384px;
    }

    :host([orientation='vertical'][variant='square']) {
      max-width: 296px; /* Figma: Image top desktop */
    }

    @container (max-width: 383px) {
      :host([variant='circular']),
      :host([orientation='horizontal'][variant='square']) {
        max-width: 304px;
      }

      :host([orientation='vertical'][variant='square']) {
        max-width: 343px; /* Figma: Image top mobile */
      }
    }

    /* ── Image frame — circular (default) ────────────────────── */
    [part='image-frame'] {
      flex-shrink: 0;
      display: block;
      margin: 0;
      padding: 0;
      width: var(--asc-person-image-size, var(--_avatar-size));
      height: var(--asc-person-image-size, var(--_avatar-size));
      background-color: var(--asc-bg-tertiary);
      overflow: hidden;
      border: var(--asc-person-image-border-width, var(--asc-border-width)) solid
        var(--asc-person-image-border-color, var(--asc-border-tertiary));
      border-radius: var(--asc-radius-full);
    }

    /* ── Image frame — vertical+square / "Image top" (full-width) ─────────── */
    :host([orientation='vertical'][variant='square']) [part='image-frame'] {
      width: 100%;
      height: auto;
      aspect-ratio: var(--asc-person-image-aspect-ratio, 1 / 1);
      border: none;
      border-radius: var(--asc-person-image-radius, var(--asc-radius-none));
    }

    /* ── Image frame — horizontal+square (fixed avatar size, square corners) ── */
    :host([orientation='horizontal'][variant='square']) [part='image-frame'] {
      border-radius: var(--asc-person-image-radius, var(--asc-radius-none));
    }

    [part='image-frame'] ::slotted(img) {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
    }

    /* ── Hidden state overrides ───────────────────────────────── */
    /*
     * Our own display declarations above win over the UA [hidden] rule.
     * Re-apply display: none explicitly so ?hidden bindings work correctly.
     */
    [part='image-frame'][hidden],
    [part='name'][hidden],
    [part='title'][hidden],
    [part='description'][hidden],
    [part='socials'][hidden] {
      display: none;
    }

    .text-group[hidden],
    .name-role[hidden] {
      display: none;
    }

    /* ── Content block ────────────────────────────────────────── */
    [part='content'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-person-content-gap, var(--asc-spacing-xl));
      min-width: 0;
      flex: 1;
    }

    /* ── Text group: name+title pair + description ────────────── */
    .text-group {
      display: flex;
      flex-direction: column;
      gap: var(--asc-person-text-group-gap, var(--asc-spacing-md));
    }

    /* ── Name-role pair: name and title stack without gap ─────── */
    .name-role {
      display: flex;
      flex-direction: column;
    }

    /* ── Name ─────────────────────────────────────────────────── */
    [part='name'] ::slotted(*) {
      margin: 0;
      padding: 0;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-person-name-font-size, var(--asc-font-size-text-lg));
      font-weight: var(--asc-person-name-font-weight, var(--asc-font-weight-bold));
      line-height: var(--asc-line-height-text-lg);
      color: var(--asc-person-name-color, var(--asc-text-primary));
    }

    /* ── Title / role ─────────────────────────────────────────── */
    [part='title'] ::slotted(*) {
      margin: 0;
      padding: 0;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-person-title-font-size, var(--asc-font-size-text-md));
      font-weight: var(--asc-person-title-font-weight, var(--asc-font-weight-medium));
      line-height: var(--asc-line-height-text-md);
      color: var(--asc-person-title-color, var(--asc-text-brand-secondary));
    }

    /* ── Description ──────────────────────────────────────────── */
    [part='description'] ::slotted(*) {
      margin: 0;
      padding: 0;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-person-description-font-size, var(--asc-font-size-text-md));
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--asc-line-height-text-md);
      color: var(--asc-person-description-color, var(--asc-text-tertiary));
    }

    /* ── Socials ──────────────────────────────────────────────── */
    [part='socials'] {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--asc-person-social-gap, var(--asc-spacing-xl));
    }

    :host([orientation='vertical'][variant='square']) [part='socials'] {
      gap: var(--asc-person-social-gap, var(--asc-spacing-2xl));
    }

    [part='socials'] ::slotted(a) {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      /*
       * 24×24px minimum meets WCAG 2.5.8 AA target size.
       * overflow must NOT be hidden here — it clips the focus ring in Chromium.
       */
      width: var(--asc-person-social-icon-size, var(--asc-spacing-3xl));  /* 24px */
      height: var(--asc-person-social-icon-size, var(--asc-spacing-3xl)); /* 24px */
      color: var(--asc-person-social-color, var(--asc-fg-tertiary));
      opacity: var(--asc-person-social-opacity, 0.5);
      text-decoration: none;
      border-radius: var(--asc-radius-xxs);
      outline: none;
      transition:
        color var(--asc-person-social-transition, 150ms ease),
        opacity var(--asc-person-social-transition, 150ms ease);
    }

    [part='socials'] ::slotted(a:hover) {
      color: var(--asc-person-social-color-hover, var(--asc-fg-primary));
      opacity: var(--asc-person-social-opacity-hover, 1);
    }

    [part='socials'] ::slotted(a:focus-visible) {
      color: var(--asc-person-social-color-hover, var(--asc-fg-primary));
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }
  `;

  /** Layout orientation — `horizontal` places the image to the left of content; `vertical` stacks the image above content. */
  @property({ type: String, reflect: true }) orientation: PersonOrientation = 'horizontal';

  /**
   * Controls image shape and display mode. `circular` (default) renders a fixed-size 96px/80px
   * avatar clipped to a full circle. `square` renders a full-width edge-to-edge photo with square
   * corners (the "Image top" Figma variant).
   */
  @property({ type: String, reflect: true }) variant: PersonVariant = 'circular';

  @state() private _hasImage = false;
  @state() private _hasName = false;
  @state() private _hasTitle = false;
  @state() private _hasDescription = false;
  @state() private _hasSocials = false;

  private get _hasNameOrTitle(): boolean {
    return this._hasName || this._hasTitle;
  }

  private get _hasTextContent(): boolean {
    return this._hasName || this._hasTitle || this._hasDescription;
  }

  private _handleSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    const hasContent = slot.assignedElements({ flatten: true }).length > 0;

    switch (slot.name) {
      case 'image':       this._hasImage = hasContent; break;
      case 'name':        this._hasName = hasContent; break;
      case 'title':       this._hasTitle = hasContent; break;
      case 'description': this._hasDescription = hasContent; break;
      case 'socials':     this._hasSocials = hasContent; break;
    }
  }

  override render() {
    return html`
      <article part="base">
        <figure
          part="image-frame"
          aria-hidden="true"
          ?hidden=${!this._hasImage}
        >
          <slot name="image" @slotchange=${this._handleSlotChange}></slot>
        </figure>

        <div part="content">
          <div class="text-group" ?hidden=${!this._hasTextContent}>
            <div class="name-role" ?hidden=${!this._hasNameOrTitle}>
              <div part="name" ?hidden=${!this._hasName}>
                <slot name="name" @slotchange=${this._handleSlotChange}></slot>
              </div>
              <div part="title" ?hidden=${!this._hasTitle}>
                <slot name="title" @slotchange=${this._handleSlotChange}></slot>
              </div>
            </div>
            <div part="description" ?hidden=${!this._hasDescription}>
              <slot name="description" @slotchange=${this._handleSlotChange}></slot>
            </div>
          </div>

          <div part="socials" ?hidden=${!this._hasSocials}>
            <slot name="socials" @slotchange=${this._handleSlotChange}></slot>
          </div>
        </div>
      </article>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-person': AscPerson;
  }
}
